import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { WorkoutFormData } from '../../types';
import Navbar from '../Layout/Navbar';
import { useUnits } from '../../contexts/UnitsContext';
import './LogWorkoutForm.css';

const LogWorkoutForm: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [restDayWarning, setRestDayWarning] = useState<string | null>(null);
  const { isMetric, distLabel, elevLabel, inputDistToMiles, inputElevToFeet, milesToDisplay, feetToDisplay } = useUnits();

  const [formData, setFormData] = useState<WorkoutFormData>({
    workout_date: new Date().toISOString().split('T')[0],
    distance_miles: 0,
    duration_seconds: 0,
    avg_heart_rate: undefined,
    max_heart_rate: undefined,
    elevation_gain_ft: undefined,
    perceived_effort: undefined,
    workout_type: undefined,
    notes: undefined
  });

  const [duration, setDuration] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [calculatedPace, setCalculatedPace] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? undefined : value
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (value === '') {
      setFormData(prev => ({ ...prev, [name]: 0 }));
      return;
    }

    const numValue = parseFloat(value);

    if (!isNaN(numValue)) {
      if (name === 'distance_miles') {
        const miles = inputDistToMiles(numValue);
        setFormData(prev => ({ ...prev, distance_miles: miles }));
        if (miles > 0) calculatePace(miles, formData.duration_seconds);
      } else if (name === 'elevation_gain_ft') {
        setFormData(prev => ({ ...prev, elevation_gain_ft: inputElevToFeet(numValue) }));
      } else {
        setFormData(prev => ({ ...prev, [name]: numValue }));
      }
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseInt(value) || 0;

    const newDuration = {
      ...duration,
      [name]: numValue
    };
    setDuration(newDuration);

    // Calculate total seconds
    const totalSeconds = newDuration.hours * 3600 + newDuration.minutes * 60 + newDuration.seconds;
    setFormData(prev => ({ ...prev, duration_seconds: totalSeconds }));

    // Recalculate pace
    if (formData.distance_miles > 0) {
      calculatePace(formData.distance_miles, totalSeconds);
    }
  };

  const calculatePace = (distanceMiles: number, durationSec: number) => {
    if (distanceMiles > 0 && durationSec > 0) {
      const paceSeconds = durationSec / distanceMiles;
      const displayPace = isMetric ? paceSeconds * 0.621371 : paceSeconds;
      const unit = isMetric ? 'km' : 'mi';
      const minutes = Math.floor(displayPace / 60);
      const seconds = Math.floor(displayPace % 60);
      setCalculatedPace(`${minutes}:${seconds.toString().padStart(2, '0')}/${unit}`);
    } else {
      setCalculatedPace('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Validate
    if (formData.distance_miles <= 0) {
      setError('Distance must be greater than 0');
      setSaving(false);
      return;
    }

    if (formData.duration_seconds <= 0) {
      setError('Duration must be greater than 0');
      setSaving(false);
      return;
    }

    try {
      const response = await api.logWorkout(formData);

      // Check for rest day warning
      if (response.rest_day_warning) {
        setRestDayWarning(response.rest_day_warning);
        setSuccessMessage('Workout logged successfully!');
        // Show warning for 5 seconds then navigate
        setTimeout(() => {
          navigate('/dashboard');
        }, 5000);
      } else {
        // No warning, navigate immediately
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to log workout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="log-workout">
        <div className="log-workout-container">
          <h1>Log Workout</h1>
          <p className="subtitle">Record your running activity</p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}
          {restDayWarning && <div className="warning-message">{restDayWarning}</div>}

          <form onSubmit={handleSubmit} className="workout-form">
            {/* Basic Info */}
            <section className="form-section">
              <h2>Workout Details</h2>

              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="workout_date"
                  value={formData.workout_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Distance ({distLabel}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="distance_miles"
                    value={formData.distance_miles ? milesToDisplay(formData.distance_miles) : ''}
                    onChange={handleNumberChange}
                    placeholder={isMetric ? 'e.g., 8.4' : 'e.g., 5.2'}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Elevation Gain ({elevLabel})</label>
                  <input
                    type="number"
                    name="elevation_gain_ft"
                    value={formData.elevation_gain_ft ? feetToDisplay(formData.elevation_gain_ft) : ''}
                    onChange={handleNumberChange}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Duration *</label>
                <div className="duration-inputs">
                  <input
                    type="number"
                    name="hours"
                    min="0"
                    value={duration.hours}
                    onChange={handleDurationChange}
                    placeholder="HH"
                  />
                  <span>:</span>
                  <input
                    type="number"
                    name="minutes"
                    min="0"
                    max="59"
                    value={duration.minutes}
                    onChange={handleDurationChange}
                    placeholder="MM"
                  />
                  <span>:</span>
                  <input
                    type="number"
                    name="seconds"
                    min="0"
                    max="59"
                    value={duration.seconds}
                    onChange={handleDurationChange}
                    placeholder="SS"
                  />
                </div>
                {calculatedPace && (
                  <div className="calculated-pace">Pace: {calculatedPace}</div>
                )}
              </div>
            </section>

            {/* Heart Rate */}
            <section className="form-section">
              <h2>Heart Rate (Optional)</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>Average HR</label>
                  <input
                    type="number"
                    name="avg_heart_rate"
                    value={formData.avg_heart_rate || ''}
                    onChange={handleNumberChange}
                    placeholder="bpm"
                  />
                </div>

                <div className="form-group">
                  <label>Max HR</label>
                  <input
                    type="number"
                    name="max_heart_rate"
                    value={formData.max_heart_rate || ''}
                    onChange={handleNumberChange}
                    placeholder="bpm"
                  />
                </div>
              </div>
            </section>

            {/* Workout Type & Effort */}
            <section className="form-section">
              <h2>Workout Classification</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>Workout Type</label>
                  <select
                    name="workout_type"
                    value={formData.workout_type || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select type</option>
                    <option value="Easy">Easy</option>
                    <option value="Long Run">Long Run</option>
                    <option value="Tempo">Tempo</option>
                    <option value="Intervals">Intervals</option>
                    <option value="Hill Repeats">Hill Repeats</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Perceived Effort</label>
                  <select
                    name="perceived_effort"
                    value={formData.perceived_effort || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select effort</option>
                    <option value="Great">Great</option>
                    <option value="Good">Good</option>
                    <option value="OK">OK</option>
                    <option value="Hard">Hard</option>
                    <option value="Terrible">Terrible</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Notes */}
            <section className="form-section">
              <h2>Notes (Optional)</h2>

              <div className="form-group">
                <textarea
                  name="notes"
                  value={formData.notes || ''}
                  onChange={handleChange}
                  rows={4}
                  placeholder="How did it feel? Weather conditions? Any observations..."
                />
              </div>
            </section>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Log Workout'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LogWorkoutForm;
